// Types shared across UI — mirror the Zod schemas + DB rows.
// Mantém em sync com packages/shared (poderíamos importar dali, mas estes são
// "view types" enxutos só com os campos que a UI consome).

export const ACCOUNT_STATUSES = [
  'available',
  'funded',
  'game_purchased',
  'in_use',
  'exhausted',
  'suspended',
  'banned',
  'retired',
] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

export const REGIONS = ['BR', 'IN', 'UA', 'ID', 'HK', 'PT', 'MX', 'AR', 'TR', 'US', 'JP', 'EU'] as const;
export type Region = (typeof REGIONS)[number];

export const RECOVERY_CODE_STATUSES = ['available', 'assigned', 'used', 'lost'] as const;
export type RecoveryCodeStatus = (typeof RECOVERY_CODE_STATUSES)[number];

export const TOPUP_STATUSES = ['available', 'reserved', 'redeemed', 'invalid', 'expired'] as const;
export type TopupStatus = (typeof TOPUP_STATUSES)[number];

/** Saldo por moeda de uma conta (Q-246): resgatado − gasto = disponível. */
export interface AccountBalance {
  currency: string;
  redeemed_total: number;
  spent_total: number;
  available: number;
}

/** Compra de jogo registrada numa conta (Q-246, automation.account_purchases). */
export interface AccountPurchase {
  id: string;
  game_title: string;
  product_ref: string | null;
  price: number;
  currency: string;
  access_count: number;
  purchased_at: string;
  purchased_by: string | null;
}

export interface PsnAccount {
  id: string;
  tenant_id: string;
  region: Region;
  email: string;
  dob: string | null;
  psn_online_id: string | null;
  status: AccountStatus;
  created_method: 'manual' | 'automated';
  phone_provider: string | null;
  proxy_id: string | null;
  adspower_profile_id: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  first_used_at: string | null;
  last_action_at: string | null;
  /** Q-246: presente em list e detail quando o backend novo estiver no ar. */
  balance?: AccountBalance[];
  /** Q-246: só no detail. */
  purchases?: AccountPurchase[];
  /** Contagem de recovery codes livres, quando o backend expuser na list. */
  recovery_codes_available?: number;
}

export interface PsnAccountDetail {
  data: PsnAccount;
  recovery_codes_available: number;
  recovery_codes_total: number;
}

export interface RecoveryCode {
  id: string;
  tenant_id: string;
  account_id: string;
  code_index: number;
  status: RecoveryCodeStatus;
  assigned_reference: string | null;
  assigned_at: string | null;
  assigned_by: string | null;
  used_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface RevealCredentialsResponse {
  data: {
    account_id: string;
    email: string;
    region: Region;
    status: AccountStatus;
    password: string | null;
    totp_secret: string | null;
    phone: string | null;
    phone_provider: string | null;
    revealed_at: string;
    rate_limit_remaining: number;
  };
}

export interface RevealCodeResponse {
  data: {
    account_id: string;
    code_index: number;
    status: RecoveryCodeStatus;
    assigned_reference: string | null;
    code: string | null;
    revealed_at: string;
    rate_limit_remaining: number;
  };
}

// ─── Q-246: Fila de Compras / catálogo / import ────────────────────────────

/** Item de jogo na fila de compras (GET /purchase-queue). */
export interface QueueGame {
  game_title: string;
  product_ref: string | null;
  shopify_product_id?: string | null;
  cross_gen?: boolean;
  access_count?: number;
  our_price_brl?: number | string | null;
  demand?: number;
  oldest_paid_at?: string | null;
  wishers?: number;
}

export interface GiftcardSummary {
  currency: string;
  available_count: number;
  total_face_value: number;
}

export interface PurchaseQueue {
  urgent: QueueGame[];
  on_demand: QueueGame[];
  wishlist: QueueGame[];
  giftcards: GiftcardSummary[];
}

/** Item do picker de jogo (GET /catalog/psn-games?q=). */
export interface CatalogGame {
  game_title: string;
  /** null = jogo sem linha "PS5 Primária" em prod — UI cai pro game_title. */
  product_ref: string | null;
  shopify_product_id: string | null;
  cross_gen: boolean;
  access_count: number;
  our_price_brl: number | string | null;
}

/** GET /psn-accounts/:id/import/preview */
export interface ImportPreview {
  account: { email: string; region: string };
  game: { title: string; product_ref: string | null } | null;
  slots: Array<{ slot_type: string; variant_label: string }>;
  recovery_codes: { available: number; will_link: number };
  price_paid: number | string | null;
  /** Moeda do price_paid (compra pode ser USD/TRY/etc — não assumir BRL). */
  currency?: string | null;
  warnings: string[];
  blockers: string[];
}

/** Q-412: entrega dos pedidos pending_stock do jogo importado (contagens). */
export interface ImportDeliveryStatus {
  pending_items: number;
  slots_available: number;
  delivered_from_account: number;
}

/** POST /psn-accounts/:id/import */
export interface ImportResult {
  imported: boolean;
  prod_account_id: string;
  slots_created: number;
  /** null = a contagem falhou (o import em si deu certo). */
  delivery: ImportDeliveryStatus | null;
  warnings: string[];
}

export interface AuditEntry {
  id: string;
  platform: string;
  account_id: string;
  action: string;
  actor_type: 'admin' | 'automation' | 'system';
  actor_id: string | null;
  metadata: Record<string, unknown>;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}
